import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { login, existsOrganizationByName } from "../../api/Services";

import { Alert, Button, Form, Input, message, Typography, Space, Divider } from "antd";
import {
  UserOutlined,
  LockOutlined,
  EditOutlined,
  HomeOutlined,
  EyeInvisibleOutlined,
  EyeTwoTone,
  LoginOutlined,
} from "@ant-design/icons";

import styles from "./LoginForm.module.scss";

const { Title, Text } = Typography;

const LoginForm = () => {
  const { t } = useTranslation();

  const [subDomain, setSubDomain] = useState("");
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const [form] = Form.useForm();

  useEffect(() => {
    const fullDomain = window.location.host;

    const appURL = process.env.REACT_APP_DOMAIN;

    const domainArray = fullDomain.split(".");
    const appUrlArray = appURL?.split(".");

    let possibleOrgArray = domainArray.filter((x) => !appUrlArray?.includes(x));

    if (possibleOrgArray.length == 1) {
      setSubDomain(possibleOrgArray[0]);
    }
  }, [subDomain]);

  const onFinish = async (data: any) => {
    const { username, password } = data;

    const organizationName =
      subDomain == "" ? data.organizationName : subDomain;

    try {
      setLoading(true);
      if (subDomain === "") {
        await navigateToOrganization();
      } else {
        const response = await login(username, password, organizationName);
        if (!response.status) {
          setError(true);
          setErrorMessage(response.message);
        } else {
          if (window.location.pathname === "/login") window.location.href = "/";
          else window.location.reload();
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const navigateToOrganization = async () => {
    const organizationName = form.getFieldValue("organizationName");

    const existOrganization = await existsOrganizationByName(organizationName);

    if (existOrganization) {
      const appURL = process.env.REACT_APP_DOMAIN;
      const url = `${window.location.protocol}\\//${organizationName}.${appURL}`;
      window.location.replace(url);
    } else {
      message.error(t("LoginForm-NotFoundOrganization"));
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.formWrapper}>
        <div className={styles.header}>
          <Title level={2} className={styles.title}>
            {subDomain === "" ? t("Common-Welcome") : `${t("Common-Welcome")} - ${subDomain}`}
          </Title>
          <Text type="secondary" className={styles.subtitle}>
            {subDomain === "" ? t("Common-EnterOrganization") : t("Common-SignInToContinue")}
          </Text>
        </div>

        {error && (
          <Alert
            message={t("Common-Error")}
            description={errorMessage}
            type="error"
            showIcon
            className={styles.errorAlert}
            closable
            onClose={() => setError(false)}
          />
        )}

        <Form
          name="login"
          form={form}
          layout="vertical"
          initialValues={{ remember: true }}
          onFinish={onFinish}
          autoComplete="off"
          className={styles.form}
          size="large"
        >
          {subDomain === "" ? (
            <Space direction="vertical" size="large" className={styles.fullWidth}>
              <Form.Item
                label={t("Common-OrganizationName")}
                name="organizationName"
                rules={[
                  {
                    required: true,
                    message: t("Common-OrganizationNameNotEntered"),
                  },
                ]}
                className={styles.formItem}
              >
                <Input
                  prefix={<HomeOutlined className={styles.inputIcon} />}
                  placeholder={t("Common-OrganizationName")}
                  className={styles.input}
                />
              </Form.Item>

              <Form.Item className={styles.formItem}>
                <Button
                  className={styles.primaryButton}
                  onClick={navigateToOrganization}
                  type="primary"
                  htmlType="button"
                  icon={<LoginOutlined />}
                  block
                  loading={loading}
                  disabled={loading}
                >
                  {t("Common-Go")}
                </Button>
              </Form.Item>

              <Divider plain>
                <Text type="secondary">{t("Common-Or")}</Text>
              </Divider>

              <div className={styles.registerSection}>
                <Text type="secondary">{t("Common-DontHaveAccount")}</Text>
                <Button type="link" href="/register" className={styles.linkButton}>
                  {t("Common-RegisterNow")}
                </Button>
              </div>
            </Space>
          ) : (
            <Space direction="vertical" size="large" className={styles.fullWidth}>
              <Form.Item
                label={t("Common-Email")}
                name="username"
                rules={[{ required: true, message: t("Common-EmailNotEntered") }]}
                className={styles.formItem}
              >
                <Input
                  prefix={<UserOutlined className={styles.inputIcon} />}
                  placeholder={t("Common-Email")}
                  className={styles.input}
                />
              </Form.Item>

              <Form.Item
                label={t("Common-Password")}
                name="password"
                rules={[
                  { required: true, message: t("Common-PasswordNotEntered") },
                ]}
                className={styles.formItem}
              >
                <Input.Password
                  prefix={<LockOutlined className={styles.inputIcon} />}
                  placeholder={t("Common-Password")}
                  className={styles.input}
                  iconRender={(visible) =>
                    visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
                  }
                />
              </Form.Item>

              <Form.Item className={styles.formItem}>
                <Button
                  type="primary"
                  htmlType="submit"
                  className={styles.loginButton}
                  icon={<LoginOutlined />}
                  block
                  loading={loading}
                  disabled={loading}
                >
                  {t("Common-Login")}
                </Button>
              </Form.Item>

              <div className={styles.forgotPasswordSection}>
                <Button
                  type="link"
                  href="/password/reset"
                  className={styles.forgotPasswordButton}
                  icon={<LockOutlined />}
                >
                  {t("Common-ForgotPassword")}
                </Button>
              </div>
            </Space>
          )}
        </Form>
        <div className={styles.rightsText}>
          {t("Common-AllRightsReserved")}<a>BrightMarket</a>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;
